/**
 * Salvage runtime (M2-17, spec §6, plan §2.3, §2.6, §2.7, §6 M2-17): combat, death, contract and derelict wrecks,
 * the cutting beam with anchor assist, coolant vents, freed parts as physics bodies, the tractor, the secured log
 * and the wreck deltas of a save.
 *
 * Stages (registered by `registerSpaceStages`, plan §2.3):
 * - `tools` (before the Rapier step): the player's cutting beam (`ToolControl.fire` in Salvage mode) → `stepCut`,
 *   `anchorCut`, `coolantVent` (+ the M1 hit event), `partFreed`; the tractor (`ToolControl.alt`) picks the freed
 *   part nearest the reticle and changes its body's velocity toward the hold point.
 * - `physics` (after the body sync): wreck and part bodies → float64, attached parts follow their wreck, and the
 *   tractor capture: `captureCheck` against the mirror → `securedLog` + `partSecured` (the mirror's `cargoUsedT`
 *   rises at once) or `partRejected` (the tractor lets go until the trigger is pressed again).
 * - `deaths`: each non-player `kill` of this tick whose ship has a `salvage.json` layout leaves a combat wreck.
 * - `end`: combat and jettison wrecks despawn after `combatLifeSeconds` (`lootedLifeSeconds` once looted; looted death and
 *   contract wrecks too), the death wreck's one-time loot check, `wreckDespawned`.
 *
 * Tools are ship systems driven by `ToolControl` (D-TBD, plan §2.2); M2 runs them for the player only, because
 * the tractor fills the player's hold (the career mirror). Wreck and part randomness is seeded (`space.rng` for new
 * wreck seeds, `hash3` per wreck), never `sim.rng` (plan §2.11). Time is game time (`mirror.gameTime`). Every stage
 * is allocation-free (D-014) except the rare events that build or remove bodies and generate parts (a kill, a
 * freed part, a despawn), accepted like M1's spawns (D-024).
 *
 * The derelict's sections exist from creation (world furniture: no `wreckSpawned`), use the Harrow's own static
 * collider and never despawn. `setDerelict` moves them (the M3 bridge puts them on each system's derelict POI, whose
 * hull and collider the Reach draws and streams; null: a system with no derelict).
 */
import type { RigidBody } from '@dimforge/rapier3d-compat'
import { emptyContents } from '../career/ops'
import type { CargoContents, MakerId } from '../career/types'
import { applyDamage, createDamageResult } from '../combat/damage'
import { emitEvent, EV, eventSlot, firstReadable } from '../combat/events'
import { SIM_DT } from '../core/loop'
import { logger } from '../core/logger'
import { rotateVec, type Quat, type Vec3 } from '../core/math'
import { nextFloat } from '../core/rng'
import type { ModelEntry } from '../data/assetSchema'
import { COMPONENT_TYPES, MAX_ANCHORS_PER_PART, MAX_PARTS_PER_WRECK, type ComponentType } from '../data/m2Schemas'
import type { RegionId } from '../galaxy/types'
import { createRay, createRayHit, setRay } from '../physics/physics'
import { assistPick, coolantVentDamage, createAnchorPick, createCutResult, stepCut } from '../salvage/cutting'
import {
  componentRadii,
  contentsBaseValue,
  contentsMassT,
  DEFAULT_SALVAGE_REGION,
  generateDeathPod,
  generateDerelictParts,
  generateJettisonPod,
  generateWreckParts,
  makerCode,
  makerOfCode,
  partValueByIndex,
  writePartSpec,
  type DerelictSite,
  type PartSpec,
} from '../salvage/generate'
import { wreckToWorld } from '../salvage/layout'
import { scanWreck } from '../salvage/scan'
import { captureCheck, holdPointWorld, inCaptureRange, inTractorRange, tractorAccel, tractorPick } from '../salvage/tractor'
import type { Sim } from '../sim'
import { contentsHaveStory, lootedStoryContents } from '../story/items'
import { Life, Pos64, Rot, Ship, Vel } from '../world/components'
import { derelictPose, type DerelictPose } from '../world/field'
import { ToolControl } from './components'
import {
  clearNav,
  createPartPool,
  createSalvageReadout,
  createSecuredLog,
  createWreckPool,
  NAV,
  NO_CLAIM_OWNER,
  SALVAGE_STATUS,
  SECURED_LOG_SIZE,
  TOOL,
  WRECK_KIND,
  WRECK_KIND_NAMES,
  type NavTarget,
  type PartPool,
  type DerelictSpot,
  type SalvageDeltas,
  type SalvageReadout,
  type SalvageRuntime,
  type SecuredLog,
  type SpaceContext,
  type QuatTuple,
  type Vec3Tuple,
  type WreckKindCode,
  type WreckPartRecord,
  type WreckPool,
  type WreckRecord,
  type WreckScan,
} from './contracts'
import { emitSpaceEvent, HOLD_FULL_SOURCE, REJECT_REASON, SEV } from './events'
import { hash3, hashString, seededUnit } from './hash'
import { addPartVelocity, allocPartSlot, createPartBody, placeAttachedParts, releasePartSlot, removePartBody, syncPartBodies } from './parts'
import { allocWreckSlot, createWreckBody, releaseWreckSlot, removeWreckBody, setWreckPose, syncWreckBodies } from './wrecks'

// Hash salts (one per independent stream).
const SALT_LOOT = 0x5a1f100d
const SALT_CONTRACT_DEF = 0x5a1fc0de
const SALT_CONTRACT_ROT = 0x5a1f4071
/** Unit float → u32 seed. */
const U32 = 4294967296
/** Below this length a direction is undefined (a push falls back to the wreck's +Y). */
const MIN_DIR = 1e-6
const CARGO_POD_TYPE = COMPONENT_TYPES.indexOf('cargoPod')
/** A jettison pod's pose: at rest in the system frame, unrotated (M6 spec §6.4.2). */
const AT_REST: Readonly<Vec3> = { x: 0, y: 0, z: 0 }
const UNROTATED: Readonly<Quat> = { x: 0, y: 0, z: 0, w: 1 }
/** A hulk's parts (M9 CH-3a, H-10: none). */
const NO_PARTS: readonly PartSpec[] = []

/** Everything the salvage stages share (one per runtime). */
interface SalvageState {
  readonly ctx: SpaceContext
  readonly readout: SalvageReadout
  readonly wrecks: WreckPool
  readonly parts: PartPool
  readonly securedLog: SecuredLog
  readonly wreckBodies: (RigidBody | undefined)[]
  readonly partBodies: (RigidBody | undefined)[]
  /** Part slot of (wreck, part index) at `wreck * MAX_PARTS_PER_WRECK + index`; -1 once gone (secured). */
  readonly partSlot: Int32Array
  /** Type index, condition and Mk of every (wreck, part index), kept for secured parts' records. */
  readonly metaType: Uint8Array
  readonly metaCondition: Float32Array
  readonly metaMk: Uint8Array
  /** Bit `index` set = that part of the wreck is gone (secured, or lost to a full part pool). */
  readonly goneMask: Uint32Array
  /** Game time a wreck was looted. */
  readonly lootedAt: Float64Array
  /** 1 = the hulk in that slot holds an open claim (PG-2 R2-4: it counts toward `hulks.maxClaimed`, not `maxLocal`). */
  readonly hulkClaimed: Uint8Array
  /** Part radius per component type (the prop model's bounds). */
  readonly radii: number[]
  /** The model entry of each ship def (sockets and bounds for layouts and muzzles). */
  readonly shipModels: (ModelEntry | null)[]
  /** The cutting beam muzzle (`salvage.json beam.socket`) per ship def, ship-local metres (0 when missing). */
  readonly muzzle: Float32Array
  /** Where the derelict's sections sit (M1's `field.json` pose until `setDerelict`), or null for none. */
  derelict: DerelictPose | null
  /** The seed of the derelict's parts (the sim seed until `setDerelict`). */
  derelictSeed: number
  /** What the derelict's site is to the Rush-pattern roll (M8 MC-h1; `plain` until `setDerelict` names one). */
  derelictSite: DerelictSite
  /** The system's other cuttable derelicts (M8 MC-h2: a graveyard system's extra sites; none until `setDerelict` names some). */
  extras: DerelictSpot[]
  /**
   * The system's galaxy region the derelict's and the ship wrecks' parts roll their Mk for (economy plan SY-4b;
   * `setRegion`). An NPC ship has no known Mk, so its combat and contract wreck parts roll by region too (D-TBD
   * Phase 1 follow-ups); the death pod carries no Mk and its contents keep their own.
   */
  region: RegionId
  /** Next wreck key sequence number. */
  nextSeq: number
  /** Combat-ring cursor of the deaths stage. */
  killCursor: number
  /** M8 MC-f5b: who built a killed ship (`bindMakerOf`, bound by traffic), or null: kill wrecks carry no maker. */
  makerOf: ((eid: number) => MakerId | null) | null
  /** M8 TR-8b1: whether a jettison pod's owner group is alive (`bindClaimOwnerAlive`), or null: only `claimUntil` counts. */
  claimOwnerAlive: ((group: number) => boolean) | null
  /** Part slot held by the player's tractor, or -1. */
  held: number
  /** After a rejection the tractor stays off until the trigger is released. */
  tractorBlocked: boolean
  /** `holdFull` was emitted for this rejection streak. */
  holdFullLatched: boolean
}

// Module scratch (single-threaded; stages never nest).
const q: Quat = { x: 0, y: 0, z: 0, w: 1 }
const fwd: Vec3 = { x: 0, y: 0, z: 0 }
const muzzleWorld: Vec3 = { x: 0, y: 0, z: 0 }
const holdPt: Vec3 = { x: 0, y: 0, z: 0 }
const accel: Vec3 = { x: 0, y: 0, z: 0 }
const tmp: Vec3 = { x: 0, y: 0, z: 0 }
const killPos: Vec3 = { x: 0, y: 0, z: 0 }
const cut = createCutResult()
const anchorPick = createAnchorPick()
const rayHit = createRayHit()
const beamRay = createRay()
const damage = createDamageResult()

// ---------------------------------------------------------------------------------------------
// Creation
// ---------------------------------------------------------------------------------------------

export function createSalvageRuntime(ctx: SpaceContext): SalvageRuntime {
  const cfg = ctx.data.salvage
  const defs = ctx.sim.defs
  const assets = ctx.game.assets
  const shipModels = defs.map((d) => assets.models.find((m) => m.id === d.assetId) ?? null)
  const muzzle = new Float32Array(defs.length * 3)
  for (let i = 0; i < defs.length; i++) {
    const socket = shipModels[i]?.sockets.find((s) => s.name === cfg.beam.socket)
    if (!socket) continue
    muzzle[i * 3] = socket.position[0]
    muzzle[i * 3 + 1] = socket.position[1]
    muzzle[i * 3 + 2] = socket.position[2]
  }
  const slots = cfg.wrecks.maxWrecks * MAX_PARTS_PER_WRECK
  const st: SalvageState = {
    ctx,
    readout: createSalvageReadout(),
    wrecks: createWreckPool(cfg.wrecks.maxWrecks),
    parts: createPartPool(cfg.wrecks.maxParts),
    securedLog: createSecuredLog(),
    wreckBodies: new Array<RigidBody | undefined>(cfg.wrecks.maxWrecks).fill(undefined),
    partBodies: new Array<RigidBody | undefined>(cfg.wrecks.maxParts).fill(undefined),
    partSlot: new Int32Array(slots).fill(-1),
    metaType: new Uint8Array(slots),
    metaCondition: new Float32Array(slots),
    metaMk: new Uint8Array(slots),
    goneMask: new Uint32Array(cfg.wrecks.maxWrecks),
    lootedAt: new Float64Array(cfg.wrecks.maxWrecks),
    hulkClaimed: new Uint8Array(cfg.wrecks.maxWrecks),
    radii: componentRadii(ctx.data, assets),
    shipModels,
    muzzle,
    derelict: derelictPose(ctx.game.field),
    derelictSeed: ctx.sim.seed >>> 0,
    derelictSite: 'plain',
    extras: [],
    region: DEFAULT_SALVAGE_REGION,
    nextSeq: 0,
    killCursor: ctx.sim.events.head,
    makerOf: null,
    claimOwnerAlive: null,
    held: -1,
    tractorBlocked: false,
    holdFullLatched: false,
  }
  ensureDerelict(st)

  return {
    readout: st.readout,
    wrecks: st.wrecks,
    parts: st.parts,
    securedLog: st.securedLog,
    pick: (ox, oy, oz, dx, dy, dz, coneRad, maxRange, out) => pickWreck(st, ox, oy, oz, dx, dy, dz, coneRad, maxRange, out),
    scan(wreck, scannerMk, out): boolean {
      if (wreck < 0 || wreck >= st.wrecks.capacity || st.wrecks.alive[wreck] === 0) {
        out.valid = false
        return false
      }
      out.kind = st.wrecks.kind[wreck]
      return st.wrecks.kind[wreck] === WRECK_KIND.hulk ? scanHulk(wreck, scannerMk, out) : scanWreck(st.wrecks, st.parts, wreck, scannerMk, ctx.data, out)
    },
    spawnCombatWreck: (defIndex, pos, vel, rot, seed, maker) =>
      spawnShipWreck(st, WRECK_KIND.combat, defIndex, pos, vel, rot, seed >>> 0, null, maker ?? null),
    bindMakerOf(makerOf): void {
      st.makerOf = makerOf
    },
    spawnDeathWreck: (pos, vel, rot, contents) =>
      spawnShipWreck(st, WRECK_KIND.death, ctx.mirror.playerDefIndex, pos, vel, rot, newSeed(st), contents, null),
    spawnContractWreck: (contractId, marker, componentType) => spawnContractWreck(st, contractId, marker, componentType),
    removeContractWreck(contractId: string): void {
      const w = findContractWreck(st, contractId)
      if (w >= 0) despawnWreck(st, w, true)
    },
    spawnStoryWreck: (key, defId, pos, rot) => spawnStoryWreck(st, key, defId, pos, rot),
    spawnJettisonPod: (pos, contents, claimUntil, owner) => spawnJettisonPod(st, pos, contents, claimUntil, owner),
    despawnJettisonPod(wreck: number): void {
      const pool = st.wrecks
      if (wreck >= 0 && wreck < pool.capacity && pool.alive[wreck] !== 0 && pool.kind[wreck] === WRECK_KIND.jettison) despawnWreck(st, wreck, true)
    },
    bindClaimOwnerAlive(alive): void {
      st.claimOwnerAlive = alive
    },
    spawnHulk: (defIndex, pos, rot, key, claimed) => spawnHulk(st, defIndex, pos, rot, key, claimed === true),
    markHulkClaimed: (key, claimed) => markHulkClaimed(st, key, claimed),
    despawnHulk(key: string): void {
      const w = findHulk(st, key)
      if (w >= 0) despawnWreck(st, w, true)
    },
    exportDeltas: () => exportDeltas(st),
    importDeltas: (deltas) => importDeltas(st, deltas),
    setDerelict: (pose, seed, site, extras) => setDerelict(st, pose, seed, site, extras),
    setRegion(region: RegionId): void {
      st.region = region
    },
    tools: (sim) => stepTools(st, sim),
    physics: (sim) => stepPhysics(st, sim),
    deaths: (sim) => stepDeaths(st, sim),
    end: () => stepEnd(st),
  }
}

/** A fresh wreck seed from the M2 RNG (career seed; never `sim.rng`). */
function newSeed(st: SalvageState): number {
  return Math.floor(nextFloat(st.ctx.rng) * U32) >>> 0
}

/** Whether the one-time loot check of a death wreck with `seed` empties its pod (`deathWreck.lootChance`). */
export function lootCheckEmpties(seed: number, lootChance: number): boolean {
  return seededUnit(seed, 0, SALT_LOOT) < lootChance
}

// ---------------------------------------------------------------------------------------------
// Spawning and despawning (rare events: they generate parts and build bodies)
// ---------------------------------------------------------------------------------------------

/** What `placeWreck` needs besides the parts. */
interface WreckPlacement {
  kind: WreckKindCode
  defIndex: number
  section: number
  key: string
  seed: number
  pos: Readonly<Vec3>
  rot: Readonly<Quat>
  vel: Readonly<Vec3>
  born: number
  contractId: string | null
  radius: number
  /** Emit `wreckSpawned` (not for the derelict's sections). */
  announce: boolean
}

/**
 * Takes a wreck slot (a non-combat wreck evicts the oldest combat wreck when the pool is full), fills it, builds its
 * hull body (ship wrecks) and places `specs` as attached parts. Returns the slot or -1.
 */
function placeWreck(st: SalvageState, at: WreckPlacement, specs: readonly PartSpec[]): number {
  const pool = st.wrecks
  let w = allocWreckSlot(pool)
  if (w < 0 && at.kind !== WRECK_KIND.combat) {
    const oldest = oldestCombatWreck(st)
    if (oldest >= 0) {
      despawnWreck(st, oldest, true)
      w = allocWreckSlot(pool)
    }
  }
  if (w < 0) {
    logger.warn(`salvage: wreck pool full (${pool.capacity}), no wreck for ${at.key}`)
    return -1
  }
  pool.kind[w] = at.kind
  pool.defIndex[w] = at.defIndex
  pool.section[w] = at.section
  pool.seed[w] = at.seed
  pool.key[w] = at.key
  pool.contractId[w] = at.contractId
  pool.born[w] = at.born
  pool.radius[w] = at.radius
  pool.claimUntil[w] = 0
  pool.claimOwner[w] = NO_CLAIM_OWNER
  pool.partCount[w] = Math.min(specs.length, MAX_PARTS_PER_WRECK)
  setWreckPose(pool, w, at.pos.x, at.pos.y, at.pos.z, at.rot, at.vel.x, at.vel.y, at.vel.z)
  st.goneMask[w] = 0
  st.lootedAt[w] = 0
  const base = w * MAX_PARTS_PER_WRECK
  for (let k = 0; k < MAX_PARTS_PER_WRECK; k++) st.partSlot[base + k] = -1

  const sim = st.ctx.sim
  if (at.defIndex >= 0 && at.kind !== WRECK_KIND.derelict) {
    const def = sim.defs[at.defIndex]
    st.wreckBodies[w] = createWreckBody(sim.physics, pool, w, sim.shipColliders[at.defIndex] ?? null, def.colliderRadius)
  }
  for (let k = 0; k < specs.length && k < MAX_PARTS_PER_WRECK; k++) {
    const spec = specs[k]
    st.metaType[base + spec.index] = spec.typeIndex
    st.metaCondition[base + spec.index] = spec.condition
    st.metaMk[base + spec.index] = spec.mk
    const slot = allocPartSlot(st.parts)
    if (slot < 0) {
      logger.warn(`salvage: part pool full (${st.parts.capacity}), ${at.key}:${spec.index} is lost`)
      st.goneMask[w] |= 1 << spec.index
      continue
    }
    writePartSpec(st.parts, slot, w, spec)
    st.partSlot[base + spec.index] = slot
  }
  placeAttachedParts(pool, st.parts)
  if (at.announce) emitSpaceEvent(st.ctx.events, SEV.wreckSpawned, -1, w, at.defIndex, at.kind, at.pos.x, at.pos.y, at.pos.z, 0, 0)
  return w
}

/** A combat or death wreck of ship def `defIndex` (see `SalvageRuntime.spawnCombatWreck` / `spawnDeathWreck`). */
function spawnShipWreck(
  st: SalvageState,
  kind: WreckKindCode,
  defIndex: number,
  pos: Readonly<Vec3>,
  vel: Readonly<Vec3>,
  rot: Readonly<Quat>,
  seed: number,
  contents: CargoContents | null,
  maker: MakerId | null,
): number {
  const model = defIndex >= 0 ? st.shipModels[defIndex] : null
  if (!model) {
    logger.warn(`salvage: ship def ${defIndex} has no model entry, no wreck`)
    return -1
  }
  const data = st.ctx.data
  const layout = data.salvage.layoutByDef[defIndex] ?? []
  const specs =
    kind === WRECK_KIND.death
      ? [generateDeathPod(seed, layout, model, data, st.radii, contents ?? emptyContents())]
      : generateWreckParts(seed, layout, model, data, 'combat', st.radii, undefined, st.region, maker)
  const key = `${kind === WRECK_KIND.death ? 'd' : 'c'}${st.nextSeq++}`
  const born = st.ctx.mirror.gameTime
  return placeWreck(st, { kind, defIndex, section: -1, key, seed, pos, rot, vel, born, contractId: null, radius: model.bounds.radius, announce: true }, specs)
}

/**
 * A jettison pod (M8 TR-8b1; see `SalvageRuntime.spawnJettisonPod`): a hull-less wreck of one cargo pod, freed at
 * once with the cargo-pod prop's collider and radius.
 */
function spawnJettisonPod(st: SalvageState, pos: Readonly<Vec3>, contents: CargoContents, claimUntil: number, owner: number): number {
  const seed = newSeed(st)
  const spec = generateJettisonPod(seed, st.ctx.data, st.radii, contents)
  const key = `j${st.nextSeq++}`
  const born = st.ctx.mirror.gameTime
  const w = placeWreck(
    st,
    { kind: WRECK_KIND.jettison, defIndex: -1, section: -1, key, seed, pos, rot: UNROTATED, vel: AT_REST, born, contractId: null, radius: spec.radius, announce: true },
    [spec],
  )
  if (w < 0) return -1
  st.wrecks.claimUntil[w] = claimUntil
  st.wrecks.claimOwner[w] = owner
  const p = st.partSlot[w * MAX_PARTS_PER_WRECK + spec.index]
  if (p >= 0) {
    const parts = st.parts
    parts.vx[p] = 0
    parts.vy[p] = 0
    parts.vz[p] = 0
    parts.freed[p] = 1
    buildFreedBody(st, p)
  }
  return w
}

/** The live hulk keyed `key`, or -1. Allocation-free. */
function findHulk(st: SalvageState, key: string): number {
  const pool = st.wrecks
  for (let w = 0; w < pool.high; w++) if (pool.alive[w] !== 0 && pool.kind[w] === WRECK_KIND.hulk && pool.key[w] === key) return w
  return -1
}

/** How many hulks are live: all (`claimed` -1), or only those without (0) or with (1) an open claim. Allocation-free. */
function liveHulks(st: SalvageState, claimed: -1 | 0 | 1): number {
  const pool = st.wrecks
  let n = 0
  for (let w = 0; w < pool.high; w++) {
    if (pool.alive[w] !== 0 && pool.kind[w] === WRECK_KIND.hulk && (claimed < 0 || st.hulkClaimed[w] === claimed)) n++
  }
  return n
}

/**
 * Whether one more hulk may spawn (PG-2 R2-4): an unclaimed one under `hulks.maxLocal` unclaimed, an open-claim one
 * under `hulks.maxClaimed` claimed, and either only under `maxLocal + maxClaimed` in all (the pool's hard bound: an
 * abandoned claim's hulk stays, counted unclaimed). Allocation-free.
 */
function hulkRoom(st: SalvageState, claimed: boolean): boolean {
  const cfg = st.ctx.data.salvage.hulks
  if (liveHulks(st, -1) >= cfg.maxLocal + cfg.maxClaimed) return false
  return claimed ? liveHulks(st, 1) < cfg.maxClaimed : liveHulks(st, 0) < cfg.maxLocal
}

/** PG-2 R2-4 (see `SalvageRuntime.markHulkClaimed`). A rare event. */
function markHulkClaimed(st: SalvageState, key: string, claimed: boolean): boolean {
  const w = findHulk(st, key)
  if (w < 0) return false
  const flag = claimed ? 1 : 0
  if (st.hulkClaimed[w] === flag) return true
  if (claimed && liveHulks(st, 1) >= st.ctx.data.salvage.hulks.maxClaimed) return false
  st.hulkClaimed[w] = flag
  return true
}

/**
 * A hulk (M9 CH-3a; see `SalvageRuntime.spawnHulk`): the ship's hull body with no parts (H-10), at rest. Its seed is
 * the key's hash (it rolls nothing; the claim's flaws hash the hulk's own seed elsewhere, MS-2), so no RNG is drawn.
 */
function spawnHulk(st: SalvageState, defIndex: number, pos: Readonly<Vec3>, rot: Readonly<Quat>, key: string, claimed: boolean): number {
  const live = findHulk(st, key)
  if (live >= 0) {
    // A re-assert carries the claim state (the manifest's record, or the Career's open site).
    markHulkClaimed(st, key, claimed)
    return live
  }
  const model = defIndex >= 0 ? st.shipModels[defIndex] : null
  if (!model) {
    logger.warn(`salvage: hulk ${key}: ship def ${defIndex} has no model entry, no hulk`)
    return -1
  }
  if (!hulkRoom(st, claimed)) {
    logger.debug(`salvage: hulk ${key} not spawned (${claimed ? 'hulks.maxClaimed' : 'hulks.maxLocal'} live)`)
    return -1
  }
  const born = st.ctx.mirror.gameTime
  const seed = hashString(key)
  const w = placeWreck(
    st,
    { kind: WRECK_KIND.hulk, defIndex, section: -1, key, seed, pos, rot, vel: AT_REST, born, contractId: null, radius: model.bounds.radius, announce: true },
    NO_PARTS,
  )
  if (w >= 0) st.hulkClaimed[w] = claimed ? 1 : 0
  return w
}

/** A hulk's scan (M9 CH-3a, H-10): valid, its kind, and no part rows. Allocation-free. */
function scanHulk(wreck: number, scannerMk: number, out: WreckScan): boolean {
  out.valid = true
  out.wreck = wreck
  out.mk = scannerMk
  out.partCount = 0
  out.totalLow = 0
  out.totalHigh = 0
  return true
}

/** Whether wreck `w` is a jettison pod still claimed by its raiders (M8 TR-8b1). Rare (at capture); allocation-free. */
function claimHeld(st: SalvageState, w: number): boolean {
  const pool = st.wrecks
  if (pool.kind[w] !== WRECK_KIND.jettison) return false
  if (st.ctx.mirror.gameTime < pool.claimUntil[w]) return true
  const owner = pool.claimOwner[w]
  return owner !== NO_CLAIM_OWNER && st.claimOwnerAlive !== null && st.claimOwnerAlive(owner)
}

/** Ship defs a recovery wreck may be (a layout with more than a cargo pod), in def order. */
function contractDefs(st: SalvageState): number[] {
  const out: number[] = []
  const layouts = st.ctx.data.salvage.layoutByDef
  for (let i = 0; i < layouts.length; i++) {
    const layout = layouts[i]
    if (layout && st.shipModels[i] && layout.some((s) => s.type !== 'cargoPod')) out.push(i)
  }
  return out
}

/** A seeded unit quaternion (uniform, Shoemake) for a contract wreck's rotation. */
function seededRotation(seed: number): Quat {
  const u1 = seededUnit(seed, 0, SALT_CONTRACT_ROT)
  const u2 = seededUnit(seed, 1, SALT_CONTRACT_ROT) * Math.PI * 2
  const u3 = seededUnit(seed, 2, SALT_CONTRACT_ROT) * Math.PI * 2
  const a = Math.sqrt(1 - u1)
  const b = Math.sqrt(u1)
  return { x: a * Math.sin(u2), y: a * Math.cos(u2), z: b * Math.sin(u3), w: b * Math.cos(u3) }
}

function contractSpecs(st: SalvageState, defIndex: number, seed: number, componentType: ComponentType): PartSpec[] {
  const model = st.shipModels[defIndex]
  if (!model) return []
  const layout = st.ctx.data.salvage.layoutByDef[defIndex] ?? []
  return generateWreckParts(seed, layout, model, st.ctx.data, 'contract', st.radii, componentType, st.region)
}

function spawnContractWreck(st: SalvageState, contractId: string, marker: Readonly<Vec3Tuple>, componentType: ComponentType): number {
  const existing = findContractWreck(st, contractId)
  if (existing >= 0) return existing
  const defs = contractDefs(st)
  if (defs.length === 0) {
    logger.warn('salvage: no ship layout can carry a recovery part')
    return -1
  }
  const seed = hashString(contractId)
  const defIndex = defs[hash3(seed, 0, SALT_CONTRACT_DEF) % defs.length]
  const model = st.shipModels[defIndex]
  return placeWreck(
    st,
    {
      kind: WRECK_KIND.contract,
      defIndex,
      section: -1,
      key: `k:${contractId}`,
      seed,
      pos: { x: marker[0], y: marker[1], z: marker[2] },
      rot: seededRotation(seed),
      vel: { x: 0, y: 0, z: 0 },
      born: st.ctx.mirror.gameTime,
      contractId,
      radius: model ? model.bounds.radius : 0,
      announce: true,
    },
    contractSpecs(st, defIndex, seed, componentType),
  )
}

/** M7 S37d: a story wreck (see `SalvageRuntime.spawnStoryWreck`); its parts roll as a combat wreck's. */
function spawnStoryWreck(st: SalvageState, key: string, defId: string, pos: Readonly<Vec3Tuple>, rot: Readonly<QuatTuple>): number {
  const pool = st.wrecks
  for (let w = 0; w < pool.high; w++) if (pool.alive[w] !== 0 && pool.key[w] === key) return w
  const defIndex = st.ctx.sim.defs.findIndex((d) => d.id === defId)
  const model = defIndex >= 0 ? st.shipModels[defIndex] : null
  const layout = defIndex >= 0 ? st.ctx.data.salvage.layoutByDef[defIndex] : null
  if (!model || !layout) {
    logger.warn(`salvage: story wreck ${key}: ship ${defId} has no model or salvage layout`)
    return -1
  }
  const seed = hashString(key)
  return placeWreck(
    st,
    {
      kind: WRECK_KIND.contract,
      defIndex,
      section: -1,
      key,
      seed,
      pos: { x: pos[0], y: pos[1], z: pos[2] },
      rot: { x: rot[0], y: rot[1], z: rot[2], w: rot[3] },
      vel: { x: 0, y: 0, z: 0 },
      born: st.ctx.mirror.gameTime,
      contractId: null,
      radius: model.bounds.radius,
      announce: true,
    },
    generateWreckParts(seed, layout, model, st.ctx.data, 'combat', st.radii, undefined, st.region),
  )
}

function findContractWreck(st: SalvageState, contractId: string): number {
  const pool = st.wrecks
  for (let w = 0; w < pool.high; w++) if (pool.alive[w] !== 0 && pool.contractId[w] === contractId) return w
  return -1
}

function oldestCombatWreck(st: SalvageState): number {
  const pool = st.wrecks
  let best = -1
  for (let w = 0; w < pool.high; w++) {
    if (pool.alive[w] === 0 || pool.kind[w] !== WRECK_KIND.combat) continue
    if (best < 0 || pool.born[w] < pool.born[best]) best = w
  }
  return best
}

/** World origin of derelict section `s`: the derelict pose applied to the section centre. */
function sectionOrigin(st: SalvageState, pose: DerelictPose, s: number): Vec3 {
  const c = st.ctx.data.salvage.derelict.sections[s].center
  const p = pose.position
  const r = pose.rotation
  return wreckToWorld(p.x, p.y, p.z, r.x, r.y, r.z, r.w, c[0], c[1], c[2], { x: 0, y: 0, z: 0 })
}

/** Radius of a derelict section: its farthest part plus that part's radius. */
function sectionRadius(specs: readonly PartSpec[]): number {
  let r = 0
  for (const s of specs) r = Math.max(r, Math.hypot(s.localPos.x, s.localPos.y, s.localPos.z) + s.radius)
  return r
}

/** A derelict section's placement (silent: the derelict is world furniture). */
function sectionPlacement(st: SalvageState, pose: DerelictPose, s: number, seed: number, key: string, specs: readonly PartSpec[]): WreckPlacement {
  return {
    kind: WRECK_KIND.derelict,
    defIndex: -1,
    section: s,
    key,
    seed,
    pos: sectionOrigin(st, pose, s),
    rot: pose.rotation,
    vel: { x: 0, y: 0, z: 0 },
    born: st.ctx.mirror.gameTime,
    contractId: null,
    radius: sectionRadius(specs),
    announce: false,
  }
}

/**
 * The key of a derelict section: `h<seed36>:<sectionId>`. The seed keeps every system's derelict parts apart (BUG-B1);
 * the same seed (re-entering a system) gives the same key. Built only on rare events (`ensureDerelict`, `restoreWreck`).
 */
export function derelictSectionKey(seed: number, sectionId: string): string {
  return `h${(seed >>> 0).toString(36)}:${sectionId}`
}

/**
 * Creates every derelict section that is not live (fresh parts from the derelict seed), for the main derelict and each
 * extra one (M8 MC-h2). Nothing with no derelict.
 */
function ensureDerelict(st: SalvageState): void {
  if (st.derelict !== null) ensureDerelictAt(st, st.derelict, st.derelictSeed, st.derelictSite, null)
  for (const e of st.extras) ensureDerelictAt(st, e.pose, e.seed, e.site, e.seed)
}

/** Creates the missing sections of one derelict (`extraSeed`: its seed for an extra one, null for the main one; `findSection`). */
function ensureDerelictAt(st: SalvageState, pose: DerelictPose, seed: number, site: DerelictSite, extraSeed: number | null): void {
  const sections = st.ctx.data.salvage.derelict.sections
  let specs: PartSpec[][] | null = null
  for (let s = 0; s < sections.length; s++) {
    if (findSection(st, s, extraSeed) >= 0) continue
    specs ??= generateDerelictParts(seed, st.ctx.data, st.radii, st.region, site)
    placeWreck(st, sectionPlacement(st, pose, s, seed, derelictSectionKey(seed, sections[s].id), specs[s]), specs[s])
  }
}

/** The extra derelict whose parts come from `seed`, or -1 (the main derelict, or none). Allocation-free. */
function extraOfSeed(st: SalvageState, seed: number): number {
  const extras = st.extras
  for (let i = 0; i < extras.length; i++) if (extras[i].seed === seed) return i
  return -1
}

/** Whether derelict wreck `w` sits at its section's place at `pose`. */
function sectionAt(st: SalvageState, w: number, pose: DerelictPose): boolean {
  const pool = st.wrecks
  const o = sectionOrigin(st, pose, pool.section[w])
  return Math.hypot(pool.x[w] - o.x, pool.y[w] - o.y, pool.z[w] - o.z) <= SAME_SECTION_M
}

const NO_SPOTS: readonly DerelictSpot[] = Object.freeze([])

/** How far (m) a live section may sit from its place at a new pose and still count as that derelict's section. */
const SAME_SECTION_M = 1

/**
 * Moves the derelict to `pose` with parts from `seed` (null: no derelict). Live sections already at that pose with
 * that seed stay (a load or a restored system world keeps its cut progress); any other goes silently, and the missing
 * sections are created fresh. `site` is what the site is to the Rush-pattern roll (M8 MC-h1: `systemWorld.ts
 * salvageSiteOf` passes it through; the same system always names the same site). `extras` (M8 MC-h2) are the system's
 * other cuttable derelicts: the same rules per derelict, by seed; they never move or change the main one. A rare event
 * (a system switch or a career start).
 */
function setDerelict(
  st: SalvageState,
  pose: DerelictPose | null,
  seed: number,
  site: DerelictSite = 'plain',
  extras: readonly DerelictSpot[] = NO_SPOTS,
): void {
  const s32 = seed >>> 0
  const pool = st.wrecks
  // M8 MC-h2: the extra derelicts first, so a live section of one at its pose keeps its progress too. None without a
  // main derelict, and never one with the main derelict's seed.
  st.extras = (pose === null ? NO_SPOTS : extras)
    .filter((e) => e.seed >>> 0 !== s32)
    .map((e) => ({ pose: { position: { ...e.pose.position }, rotation: { ...e.pose.rotation } }, seed: e.seed >>> 0, site: e.site }))
  for (let w = 0; w < pool.high; w++) {
    if (pool.alive[w] === 0 || pool.kind[w] !== WRECK_KIND.derelict) continue
    const e = extraOfSeed(st, pool.seed[w])
    if (e >= 0 ? sectionAt(st, w, st.extras[e].pose) : pose !== null && pool.seed[w] === s32 && sectionAt(st, w, pose)) continue
    despawnWreck(st, w, false)
  }
  st.derelict = pose === null ? null : { position: { ...pose.position }, rotation: { ...pose.rotation } }
  st.derelictSeed = s32
  st.derelictSite = site
  ensureDerelict(st)
}

/**
 * The live derelict wreck of section `s`, or -1: the one with parts from `seed`, or (null) the main derelict's, which
 * is any derelict section whose seed is no extra's (so an old save's sections stay the main one's, as before M8 MC-h2).
 */
function findSection(st: SalvageState, s: number, seed: number | null): number {
  const pool = st.wrecks
  for (let w = 0; w < pool.high; w++) {
    if (pool.alive[w] === 0 || pool.kind[w] !== WRECK_KIND.derelict || pool.section[w] !== s) continue
    if (seed === null ? extraOfSeed(st, pool.seed[w]) < 0 : pool.seed[w] === seed) return w
  }
  return -1
}

/** Removes part slot `p` and its body (the part is gone: secured, or its wreck despawned). */
function removePart(st: SalvageState, p: number): void {
  const parts = st.parts
  const body = st.partBodies[p]
  if (body !== undefined) {
    removePartBody(st.ctx.sim.physics, body)
    st.partBodies[p] = undefined
  }
  if (st.held === p) st.held = -1
  const k = parts.wreck[p] * MAX_PARTS_PER_WRECK + parts.index[p]
  if (st.partSlot[k] === p) st.partSlot[k] = -1
  clearNavIf(st, NAV.part, p)
  releasePartSlot(parts, p)
}

/** Despawns wreck `w`: every part of it (attached or freed), its body, nav targets on it; `wreckDespawned` when `announce`. */
function despawnWreck(st: SalvageState, w: number, announce: boolean): void {
  const pool = st.wrecks
  if (pool.alive[w] === 0) return
  const parts = st.parts
  for (let p = 0; p < parts.high; p++) if (parts.alive[p] !== 0 && parts.wreck[p] === w) removePart(st, p)
  const body = st.wreckBodies[w]
  if (body !== undefined) {
    removeWreckBody(st.ctx.sim.physics, body)
    st.wreckBodies[w] = undefined
  }
  clearNavIf(st, NAV.wreck, w)
  if (announce) emitSpaceEvent(st.ctx.events, SEV.wreckDespawned, -1, w, pool.defIndex[w], pool.kind[w], pool.x[w], pool.y[w], pool.z[w], 0, 0)
  releaseWreckSlot(pool, w)
}

/** Clears the player's nav target (and last scan) when it points at (`kind`, `index`). Allocation-free. */
function clearNavIf(st: SalvageState, kind: number, index: number): void {
  const player = st.ctx.player
  if (player.scanned.kind === kind && player.scanned.index === index) clearNav(player.scanned)
  if (player.nav.kind !== kind || player.nav.index !== index) return
  clearNav(player.nav)
  emitSpaceEvent(st.ctx.events, SEV.navTarget, st.ctx.sim.playerEid, NAV.none, -1, 0, 0, 0, 0, 0, 0)
}

/** Frees part `p` (its last anchor was cut): a dynamic body with the wreck's velocity plus `freedPush` outward. */
function freePart(st: SalvageState, p: number): void {
  const parts = st.parts
  const wrecks = st.wrecks
  const w = parts.wreck[p]
  let nx = parts.x[p] - wrecks.x[w]
  let ny = parts.y[p] - wrecks.y[w]
  let nz = parts.z[p] - wrecks.z[w]
  const len = Math.sqrt(nx * nx + ny * ny + nz * nz)
  if (len < MIN_DIR) {
    q.x = wrecks.qx[w]
    q.y = wrecks.qy[w]
    q.z = wrecks.qz[w]
    q.w = wrecks.qw[w]
    rotateVec(q, 0, 1, 0, tmp)
    nx = tmp.x
    ny = tmp.y
    nz = tmp.z
  } else {
    nx /= len
    ny /= len
    nz /= len
  }
  const push = st.ctx.data.salvage.wrecks.freedPush
  parts.vx[p] = wrecks.vx[w] + nx * push
  parts.vy[p] = wrecks.vy[w] + ny * push
  parts.vz[p] = wrecks.vz[w] + nz * push
  parts.freed[p] = 1
  buildFreedBody(st, p)
}

/** The dynamic body of freed part `p`: its prop's collider (else a ball of its radius) at its pose and velocity. */
function buildFreedBody(st: SalvageState, p: number): void {
  const type = st.parts.type[p]
  const model = st.ctx.models.get(st.ctx.data.wares.components[type].propAsset)
  st.partBodies[p] = createPartBody(st.ctx.sim.physics, st.parts, p, model?.collider ?? null, st.radii[type])
}

/**
 * The death wreck's loot check found raiders: every unsecured pod of wreck `w` is emptied. M7 (spec §2.8, SL-2): a
 * `story` pod (one carrying story items) skips the loot roll for them: its story components stay, the rest goes.
 */
function emptyPods(st: SalvageState, w: number): void {
  const parts = st.parts
  for (let p = 0; p < parts.high; p++) {
    const contents = parts.contents[p]
    if (parts.alive[p] === 0 || parts.wreck[p] !== w || contents === null) continue
    parts.contents[p] = contentsHaveStory(contents) ? lootedStoryContents(contents, emptyContents()) : emptyContents()
  }
}

// ---------------------------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------------------------

/**
 * The wreck nearest the ray within `coneRad` (angle to its bounding sphere), else the nearest within `maxRange`
 * (distance to its bounding sphere). Allocation-free.
 */
function pickWreck(
  st: SalvageState,
  ox: number,
  oy: number,
  oz: number,
  dx: number,
  dy: number,
  dz: number,
  coneRad: number,
  maxRange: number,
  out: NavTarget,
): boolean {
  const pool = st.wrecks
  let best = -1
  let bestAngle = Infinity
  let near = -1
  let nearDist = Infinity
  for (let w = 0; w < pool.high; w++) {
    if (pool.alive[w] === 0) continue
    const rx = pool.x[w] - ox
    const ry = pool.y[w] - oy
    const rz = pool.z[w] - oz
    const d = Math.sqrt(rx * rx + ry * ry + rz * rz)
    const r = pool.radius[w]
    const surface = d - r
    if (surface > maxRange) continue
    if (surface < nearDist) {
      nearDist = surface
      near = w
    }
    let angle = 0
    if (d > r) {
      let cos = (rx * dx + ry * dy + rz * dz) / d
      if (cos > 1) cos = 1
      else if (cos < -1) cos = -1
      angle = Math.acos(cos) - Math.asin(r / d)
    }
    if (angle <= coneRad && angle < bestAngle) {
      bestAngle = angle
      best = w
    }
  }
  const pick = best >= 0 ? best : near
  if (pick < 0) return false
  out.kind = NAV.wreck
  out.index = pick
  return true
}

// ---------------------------------------------------------------------------------------------
// Stages (allocation-free)
// ---------------------------------------------------------------------------------------------

/** Loads player `p`'s rotation, nose direction and cutting-beam muzzle (world) into the module scratch. */
function loadPlayerFrame(st: SalvageState, p: number): void {
  q.x = Rot.x[p]
  q.y = Rot.y[p]
  q.z = Rot.z[p]
  q.w = Rot.w[p]
  rotateVec(q, 0, 0, -1, fwd)
  const m = Ship.defIndex[p] * 3
  rotateVec(q, st.muzzle[m], st.muzzle[m + 1], st.muzzle[m + 2], muzzleWorld)
  muzzleWorld.x += Pos64.x[p]
  muzzleWorld.y += Pos64.y[p]
  muzzleWorld.z += Pos64.z[p]
}

/** Lets go of the tractored part (it keeps drifting). */
function releaseTractor(st: SalvageState): void {
  if (st.held >= 0) st.parts.tractored[st.held] = 0
  st.held = -1
}

/** The tractor trigger is up (or Salvage mode is off): let go and re-arm it. */
function tractorOff(st: SalvageState): void {
  releaseTractor(st)
  st.readout.tractorOn = false
  st.readout.tractorPart = -1
  st.tractorBlocked = false
}

function stepTools(st: SalvageState, sim: Sim): void {
  const r = st.readout
  r.beamOn = false
  r.wreck = -1
  r.part = -1
  r.anchor = -1
  r.cutProgress = 0
  r.spilling = false
  if (st.held >= 0) st.parts.tractored[st.held] = 0
  const p = sim.playerEid
  if (p < 0 || Life.alive[p] !== 1 || ToolControl.mode[p] !== TOOL.salvage) {
    tractorOff(st)
    return
  }
  loadPlayerFrame(st, p)
  if (ToolControl.fire[p] !== 0) stepBeam(st, sim, p)
  if (ToolControl.alt[p] !== 0) stepTractor(st, p)
  else tractorOff(st)
}

/** The cutting beam of player `p` this tick (anchor assist → `stepCut` → anchor, coolant and freed-part events). */
function stepBeam(st: SalvageState, sim: Sim, p: number): void {
  const r = st.readout
  const cfg = st.ctx.data.salvage
  const tools = st.ctx.mirror.tools
  const ox = muzzleWorld.x
  const oy = muzzleWorld.y
  const oz = muzzleWorld.z
  let dx = fwd.x
  let dy = fwd.y
  let dz = fwd.z
  if (assistPick(st.parts, -1, ox, oy, oz, dx, dy, dz, cfg.beam.range, cfg.beam.assist, anchorPick)) {
    const ax = anchorPick.x - ox
    const ay = anchorPick.y - oy
    const az = anchorPick.z - oz
    const len = Math.sqrt(ax * ax + ay * ay + az * az)
    dx = ax / len
    dy = ay / len
    dz = az / len
  }
  stepCut(st.parts, -1, ox, oy, oz, dx, dy, dz, SIM_DT, tools.cutMult, tools.spillPerSec, cfg.beam, cut)
  r.beamOn = true
  r.beamFrom.x = ox
  r.beamFrom.y = oy
  r.beamFrom.z = oz
  r.beamTo.x = cut.hitX
  r.beamTo.y = cut.hitY
  r.beamTo.z = cut.hitZ
  if (!cut.contact) {
    // No anchor: the beam ends on whatever hull it meets.
    if (sim.raycast(setRay(beamRay, ox, oy, oz, dx, dy, dz, cfg.beam.range), p, rayHit)) {
      r.beamTo.x = rayHit.x
      r.beamTo.y = rayHit.y
      r.beamTo.z = rayHit.z
    }
    return
  }
  const parts = st.parts
  const part = cut.part
  r.part = part
  r.anchor = cut.anchor
  r.cutProgress = cut.progress
  r.spilling = cut.spilling
  r.wreck = parts.wreck[part]
  if (!cut.anchorCut) return
  const i = part * MAX_ANCHORS_PER_PART + cut.anchor
  const ax = parts.ax[i]
  const ay = parts.ay[i]
  const az = parts.az[i]
  emitSpaceEvent(st.ctx.events, SEV.anchorCut, p, part, cut.anchor, cut.coolant ? 1 : 0, ax, ay, az, 0, 0)
  if (cut.coolant) ventCoolant(st, sim, p, part, ax, ay, az)
  if (cut.partFreed) {
    freePart(st, part)
    emitSpaceEvent(st.ctx.events, SEV.partFreed, p, part, 0, 0, parts.x[part], parts.y[part], parts.z[part], 0, 0)
  }
}

/** A coolant anchor was cut at (vx, vy, vz): the vent damages player `p` within `coolant.radius` (+ the M1 hit event). */
function ventCoolant(st: SalvageState, sim: Sim, p: number, part: number, vx: number, vy: number, vz: number): void {
  const px = Pos64.x[p]
  const py = Pos64.y[p]
  const pz = Pos64.z[p]
  const dmg = coolantVentDamage(vx, vy, vz, px, py, pz, st.ctx.data.salvage.coolant)
  if (dmg > 0) {
    applyDamage(p, dmg, 1, 1, sim.time, damage)
    let nx = px - vx
    let ny = py - vy
    let nz = pz - vz
    const len = Math.sqrt(nx * nx + ny * ny + nz * nz)
    if (len > MIN_DIR) {
      nx /= len
      ny /= len
      nz /= len
    }
    if (damage.shieldAbsorbed > 0) emitEvent(sim.events, EV.hitShield, px, py, pz, nx, ny, nz, p, -1, 0, damage.shieldAbsorbed)
    if (damage.hullDamage > 0) emitEvent(sim.events, EV.hitHull, px, py, pz, nx, ny, nz, p, -1, 0, damage.hullDamage)
  }
  emitSpaceEvent(st.ctx.events, SEV.coolantVent, p, part, 0, 0, vx, vy, vz, dmg, 0)
}

/** The tractor of player `p`: grab (the freed part nearest the reticle), hold in range, pull toward the hold point. */
function stepTractor(st: SalvageState, p: number): void {
  const r = st.readout
  const parts = st.parts
  const cfg = st.ctx.data.salvage.tractor
  const range = st.ctx.mirror.tools.tractorRange
  r.tractorOn = true
  const held = st.held
  if (held >= 0 && (parts.alive[held] === 0 || parts.freed[held] === 0 || parts.secured[held] !== 0)) releaseTractor(st)
  if (st.held < 0) {
    if (st.tractorBlocked) {
      r.tractorPart = -1
      return
    }
    const pick = tractorPick(parts, muzzleWorld.x, muzzleWorld.y, muzzleWorld.z, fwd.x, fwd.y, fwd.z, range, cfg.cone)
    if (pick < 0) {
      r.tractorPart = -1
      return
    }
    st.held = pick
    r.status = SALVAGE_STATUS.ok
  }
  const s = st.held
  if (!inTractorRange(parts.x[s], parts.y[s], parts.z[s], muzzleWorld.x, muzzleWorld.y, muzzleWorld.z, range)) {
    releaseTractor(st)
    r.tractorPart = -1
    r.status = SALVAGE_STATUS.outOfRange
    return
  }
  holdPointWorld(Pos64.x[p], Pos64.y[p], Pos64.z[p], q.x, q.y, q.z, q.w, cfg, holdPt)
  tractorAccel(
    parts.x[s],
    parts.y[s],
    parts.z[s],
    parts.vx[s],
    parts.vy[s],
    parts.vz[s],
    holdPt.x,
    holdPt.y,
    holdPt.z,
    Vel.x[p],
    Vel.y[p],
    Vel.z[p],
    SIM_DT,
    cfg,
    accel,
  )
  const body = st.partBodies[s]
  if (body !== undefined) addPartVelocity(body, accel.x * SIM_DT, accel.y * SIM_DT, accel.z * SIM_DT)
  parts.tractored[s] = 1
  r.tractorPart = s
  r.tractorTo.x = parts.x[s]
  r.tractorTo.y = parts.y[s]
  r.tractorTo.z = parts.z[s]
}

function stepPhysics(st: SalvageState, sim: Sim): void {
  syncWreckBodies(sim.physics, st.wrecks, st.wreckBodies, st.ctx.data.salvage.wrecks.drag, SIM_DT)
  placeAttachedParts(st.wrecks, st.parts)
  syncPartBodies(sim.physics, st.parts, st.partBodies)
  const s = st.held
  const p = sim.playerEid
  if (s < 0 || p < 0 || Life.alive[p] !== 1) return
  const parts = st.parts
  st.readout.tractorTo.x = parts.x[s]
  st.readout.tractorTo.y = parts.y[s]
  st.readout.tractorTo.z = parts.z[s]
  const cfg = st.ctx.data.salvage.tractor
  holdPointWorld(Pos64.x[p], Pos64.y[p], Pos64.z[p], Rot.x[p], Rot.y[p], Rot.z[p], Rot.w[p], cfg, holdPt)
  if (inCaptureRange(parts.x[s], parts.y[s], parts.z[s], holdPt.x, holdPt.y, holdPt.z, cfg)) capturePart(st, p, s)
}

/**
 * The tractored part `s` reached the hold point: secure it, or reject it (a raider-claimed jettison pod first, M8
 * TR-8b1; then `captureCheck` against the mirror).
 */
function capturePart(st: SalvageState, p: number, s: number): void {
  const parts = st.parts
  const mirror = st.ctx.mirror
  const contents = parts.contents[s]
  const massT = parts.massT[s]
  const holdT = contents !== null ? massT + contentsMassT(contents) : massT
  const status = claimHeld(st, parts.wreck[s])
    ? SALVAGE_STATUS.claimed
    : captureCheck(massT, mirror.maxPartMassT, mirror.cargoCapacityT - mirror.cargoUsedT, holdT)
  st.readout.status = status
  if (status === SALVAGE_STATUS.ok) {
    securePart(st, p, s, massT, holdT)
    st.holdFullLatched = false
    return
  }
  const reason =
    status === SALVAGE_STATUS.tooLarge ? REJECT_REASON.tooLarge : status === SALVAGE_STATUS.claimed ? REJECT_REASON.claimed : REJECT_REASON.holdFull
  emitSpaceEvent(st.ctx.events, SEV.partRejected, p, s, reason, 0, parts.x[s], parts.y[s], parts.z[s], 0, 0)
  if (reason === REJECT_REASON.holdFull && !st.holdFullLatched) {
    st.holdFullLatched = true
    emitSpaceEvent(st.ctx.events, SEV.holdFull, p, HOLD_FULL_SOURCE.part, 0, 0, parts.x[s], parts.y[s], parts.z[s], 0, 0)
  }
  releaseTractor(st)
  st.readout.tractorPart = -1
  st.tractorBlocked = true
}

/**
 * Secures part `s` into the next `securedLog` slot, emits `partSecured` (g = the tonnes it takes in the hold: a pod
 * with its contents), fills the mirror's hold at once and removes the part; `wreckLooted` when it was the last.
 */
function securePart(st: SalvageState, p: number, s: number, massT: number, holdT: number): void {
  const parts = st.parts
  const wrecks = st.wrecks
  const data = st.ctx.data
  const w = parts.wreck[s]
  const index = parts.index[s]
  const type = parts.type[s]
  const condition = parts.condition[s]
  const mk = parts.mk[s]
  const contents = parts.contents[s]
  const value = contents !== null ? contentsBaseValue(contents, data) : partValueByIndex(type, condition, data)
  const log = st.securedLog
  const slot = log.head % SECURED_LOG_SIZE
  const entry = log.entries[slot]
  entry.key = wrecks.key[w] + ':' + index
  entry.type = COMPONENT_TYPES[type]
  entry.condition = condition
  entry.mk = mk
  entry.maker = makerOfCode(parts.maker[s])
  entry.rush = parts.rush[s] !== 0
  entry.massT = massT
  entry.value = value
  entry.contractId = wrecks.contractId[w]
  entry.contents = contents
  log.head++
  emitSpaceEvent(st.ctx.events, SEV.partSecured, p, type, Math.round(condition * 1000), slot, parts.x[s], parts.y[s], parts.z[s], value, holdT)
  st.ctx.mirror.cargoUsedT += holdT
  const k = w * MAX_PARTS_PER_WRECK + index
  st.metaType[k] = type
  st.metaCondition[k] = condition
  st.metaMk[k] = mk
  st.goneMask[w] |= 1 << index
  parts.secured[s] = 1
  removePart(st, s)
  st.readout.tractorPart = -1
  if (wrecks.looted[w] === 0 && wreckEmpty(st, w)) {
    wrecks.looted[w] = 1
    st.lootedAt[w] = st.ctx.mirror.gameTime
    emitSpaceEvent(st.ctx.events, SEV.wreckLooted, -1, w, wrecks.defIndex[w], wrecks.kind[w], wrecks.x[w], wrecks.y[w], wrecks.z[w], 0, 0)
  }
}

/** Every generated part of wreck `w` is gone. */
function wreckEmpty(st: SalvageState, w: number): boolean {
  const full = (1 << st.wrecks.partCount[w]) - 1
  return (st.goneMask[w] & full) === full
}

function stepDeaths(st: SalvageState, sim: Sim): void {
  const ring = sim.events
  const layouts = st.ctx.data.salvage.layoutByDef
  for (let i = firstReadable(ring, st.killCursor); i < ring.head; i++) {
    const s = eventSlot(i)
    if (ring.type[s] !== EV.kill) continue
    const eid = ring.eid[s]
    if (eid < 0 || eid === sim.playerEid || Life.alive[eid] !== 0) continue
    const def = Ship.defIndex[eid]
    const layout = layouts[def]
    if (layout === null || layout === undefined) continue
    spawnKillWreck(st, eid, def)
  }
  st.killCursor = ring.head
}

/**
 * A combat wreck at killed ship `eid`'s pose and velocity (its SoA columns survive the despawn this tick). Its parts
 * carry the maker the bound `makerOf` hook names (M8 MC-f5b; none when unbound or for raiders, guardians, corp ships).
 */
function spawnKillWreck(st: SalvageState, eid: number, def: number): void {
  q.x = Rot.x[eid]
  q.y = Rot.y[eid]
  q.z = Rot.z[eid]
  q.w = Rot.w[eid]
  killPos.x = Pos64.x[eid]
  killPos.y = Pos64.y[eid]
  killPos.z = Pos64.z[eid]
  tmp.x = Vel.x[eid]
  tmp.y = Vel.y[eid]
  tmp.z = Vel.z[eid]
  const maker = st.makerOf !== null ? st.makerOf(eid) : null
  spawnShipWreck(st, WRECK_KIND.combat, def, killPos, tmp, q, newSeed(st), null, maker)
}

function stepEnd(st: SalvageState): void {
  const pool = st.wrecks
  const cfg = st.ctx.data.salvage
  const now = st.ctx.mirror.gameTime
  for (let w = 0; w < pool.high; w++) {
    if (pool.alive[w] === 0) continue
    const kind = pool.kind[w]
    // M9 CH-3a: a hulk has no timer (and no parts, so it is never looted); it goes only by `despawnHulk`.
    if (kind === WRECK_KIND.derelict || kind === WRECK_KIND.hulk) continue
    if (pool.looted[w] !== 0) {
      if (now >= st.lootedAt[w] + cfg.wrecks.lootedLifeSeconds) despawnWreck(st, w, true)
      continue
    }
    if (kind === WRECK_KIND.combat || kind === WRECK_KIND.jettison) {
      if (now >= pool.born[w] + cfg.wrecks.combatLifeSeconds) despawnWreck(st, w, true)
      continue
    }
    if (kind === WRECK_KIND.death && pool.lootChecked[w] === 0 && now >= pool.born[w] + cfg.deathWreck.lootCheckSeconds) {
      pool.lootChecked[w] = 1
      if (lootCheckEmpties(pool.seed[w], cfg.deathWreck.lootChance)) emptyPods(st, w)
    }
  }
}

// ---------------------------------------------------------------------------------------------
// Deltas (save and load; not per tick)
// ---------------------------------------------------------------------------------------------

function exportDeltas(st: SalvageState): SalvageDeltas {
  const pool = st.wrecks
  const parts = st.parts
  const defs = st.ctx.sim.defs
  const sections = st.ctx.data.salvage.derelict.sections
  const wrecks: WreckRecord[] = []
  for (let w = 0; w < pool.high; w++) {
    // M8 TR-8b1: a jettison pod is never saved (a load or a system switch: the raiders took it). M9 CH-3a: nor is a
    // hulk (re-derived on entry from its seeded spot, the traffic manifest and `career.hulks`).
    if (pool.alive[w] === 0 || pool.kind[w] === WRECK_KIND.jettison || pool.kind[w] === WRECK_KIND.hulk) continue
    const records: WreckPartRecord[] = []
    const base = w * MAX_PARTS_PER_WRECK
    for (let k = 0; k < pool.partCount[w]; k++) {
      const s = st.partSlot[base + k]
      if (s < 0) {
        const gone: WreckPartRecord = { type: COMPONENT_TYPES[st.metaType[base + k]], condition: st.metaCondition[base + k], cut: [], freed: true, secured: true }
        if (st.metaMk[base + k] > 0) gone.mk = st.metaMk[base + k]
        records.push(gone)
        continue
      }
      const cutList: number[] = []
      for (let a = 0; a < parts.anchorCount[s]; a++) cutList.push(parts.cut[s * MAX_ANCHORS_PER_PART + a])
      const rec: WreckPartRecord = {
        type: COMPONENT_TYPES[parts.type[s]],
        condition: parts.condition[s],
        cut: cutList,
        freed: parts.freed[s] !== 0,
        secured: false,
      }
      if (parts.mk[s] > 0) rec.mk = parts.mk[s]
      // M8 MC-f5a: a derelict's maker re-derives from its seed, so only a ship wreck's named maker is written; the
      // Rush tag only when set (M8 MC-h1 rolls it on mid, deep and rim derelicts; a gate or sealed one never has it).
      const maker = makerOfCode(parts.maker[s])
      if (maker !== null && pool.kind[w] !== WRECK_KIND.derelict) rec.maker = maker
      if (parts.rush[s] !== 0) rec.rush = true
      if (parts.freed[s] !== 0) {
        rec.pos = [parts.x[s], parts.y[s], parts.z[s]]
        rec.vel = [parts.vx[s], parts.vy[s], parts.vz[s]]
        rec.rot = [parts.qx[s], parts.qy[s], parts.qz[s], parts.qw[s]]
      }
      const contents = parts.contents[s]
      if (contents !== null) rec.contents = structuredClone(contents)
      records.push(rec)
    }
    const record: WreckRecord = {
      key: pool.key[w],
      kind: WRECK_KIND_NAMES[pool.kind[w]],
      seed: pool.seed[w],
      pos: [pool.x[w], pool.y[w], pool.z[w]],
      vel: [pool.vx[w], pool.vy[w], pool.vz[w]],
      rot: [pool.qx[w], pool.qy[w], pool.qz[w], pool.qw[w]],
      bornAt: pool.born[w],
      lootChecked: pool.lootChecked[w] !== 0,
      parts: records,
    }
    if (pool.defIndex[w] >= 0) record.defId = defs[pool.defIndex[w]].id
    if (pool.section[w] >= 0) record.section = sections[pool.section[w]].id
    const contractId = pool.contractId[w]
    if (contractId !== null) record.contractId = contractId
    wrecks.push(record)
  }
  return { nextSeq: st.nextSeq, wrecks }
}

function importDeltas(st: SalvageState, deltas: SalvageDeltas): void {
  // A load replaces the world: every wreck goes silently, then the saved ones come back.
  const pool = st.wrecks
  for (let w = 0; w < pool.high; w++) if (pool.alive[w] !== 0) despawnWreck(st, w, false)
  tractorOff(st)
  st.holdFullLatched = false
  st.nextSeq = deltas.nextSeq
  for (const rec of deltas.wrecks) restoreWreck(st, rec)
  ensureDerelict(st)
}

/** The regenerated parts of a saved wreck, or null when its ship, model or section no longer exists. */
function recordSpecs(st: SalvageState, rec: WreckRecord, defIndex: number, section: number, site: DerelictSite): PartSpec[] | null {
  const data = st.ctx.data
  if (rec.kind === 'derelict') return section >= 0 ? generateDerelictParts(rec.seed, data, st.radii, st.region, site)[section] : null
  const model = defIndex >= 0 ? st.shipModels[defIndex] : null
  if (!model) return null
  const layout = data.salvage.layoutByDef[defIndex] ?? []
  if (rec.kind === 'death') {
    const pod = rec.parts.find((p) => p.contents !== undefined)
    return [generateDeathPod(rec.seed, layout, model, data, st.radii, pod?.contents ?? emptyContents())]
  }
  // A story wreck (a contract-kind wreck with no contract, S37d) rolled its parts as a combat wreck: the fallback below.
  if (rec.kind === 'contract' && rec.contractId !== undefined) {
    // The required type is not saved: it is the one whose regeneration matches the saved part types.
    const saved = rec.parts.map((p) => p.type)
    for (const type of new Set(saved)) {
      const specs = contractSpecs(st, defIndex, rec.seed, type)
      if (specs.length === saved.length && specs.every((s, i) => s.type === saved[i])) return specs
    }
  }
  return generateWreckParts(rec.seed, layout, model, data, 'combat', st.radii, undefined, st.region)
}

function restoreWreck(st: SalvageState, rec: WreckRecord): void {
  const defs = st.ctx.sim.defs
  const sections = st.ctx.data.salvage.derelict.sections
  const kind = WRECK_KIND[rec.kind]
  const defIndex = rec.defId === undefined ? -1 : defs.findIndex((d) => d.id === rec.defId)
  const section = rec.section === undefined ? -1 : sections.findIndex((s) => s.id === rec.section)
  // M8 MC-h2: a section of an extra derelict (by its seed) goes on that one; any other on the main derelict. A record
  // is a duplicate only of a live section with its own seed, so a load made before the extras are known (M2's own
  // load, ahead of the system's enter hook) keeps every derelict's progress; `setDerelict` then sorts them.
  const extra = kind === WRECK_KIND.derelict ? extraOfSeed(st, rec.seed >>> 0) : -1
  const spot = extra >= 0 ? st.extras[extra] : null
  if (kind === WRECK_KIND.derelict && section >= 0 && findSection(st, section, rec.seed >>> 0) >= 0) return
  const pose = spot === null ? st.derelict : spot.pose
  if (kind === WRECK_KIND.derelict && pose === null) {
    logger.debug(`salvage: saved derelict section ${rec.key} dropped (no derelict here)`)
    return
  }
  const specs = recordSpecs(st, rec, defIndex, section, spot === null ? st.derelictSite : spot.site)
  if (specs === null) {
    logger.warn(`salvage: saved wreck ${rec.key} no longer matches the data, dropped`)
    return
  }
  const pos = { x: rec.pos[0], y: rec.pos[1], z: rec.pos[2] }
  const vel = { x: rec.vel[0], y: rec.vel[1], z: rec.vel[2] }
  const rot = { x: rec.rot[0], y: rec.rot[1], z: rec.rot[2], w: rec.rot[3] }
  const seed = rec.seed >>> 0
  const at: WreckPlacement =
    kind === WRECK_KIND.derelict && pose !== null
      ? // Re-keyed from the seed: an old save's `h:<section>` key upgrades in place (BUG-B1).
        { ...sectionPlacement(st, pose, section, seed, derelictSectionKey(seed, sections[section].id), specs), pos, rot, born: rec.bornAt }
      : {
          kind,
          defIndex,
          section: -1,
          key: rec.key,
          seed,
          pos,
          rot,
          vel,
          born: rec.bornAt,
          contractId: rec.contractId ?? null,
          radius: st.shipModels[defIndex]?.bounds.radius ?? 0,
          announce: true,
        }
  const w = placeWreck(st, at, specs)
  if (w < 0) return
  st.wrecks.lootChecked[w] = rec.lootChecked ? 1 : 0
  const base = w * MAX_PARTS_PER_WRECK
  const parts = st.parts
  const n = Math.min(rec.parts.length, st.wrecks.partCount[w])
  for (let k = 0; k < n; k++) {
    const pr = rec.parts[k]
    const s = st.partSlot[base + k]
    if (pr.secured) {
      st.metaType[base + k] = COMPONENT_TYPES.indexOf(pr.type)
      st.metaCondition[base + k] = pr.condition
      // A record without a Mk keeps the one its seed re-derives (placeWreck wrote it).
      if (pr.mk !== undefined) st.metaMk[base + k] = pr.mk
      st.goneMask[w] |= 1 << k
      if (s >= 0) removePart(st, s)
      continue
    }
    if (s < 0) continue
    parts.condition[s] = pr.condition
    st.metaCondition[base + k] = pr.condition
    if (pr.mk !== undefined) {
      parts.mk[s] = pr.mk
      st.metaMk[base + k] = pr.mk
    }
    // M8 MC-f5a: a record without them (every older save) keeps what the seed re-derives (placeWreck wrote it).
    if (pr.maker !== undefined) parts.maker[s] = makerCode(pr.maker)
    if (pr.rush !== undefined) parts.rush[s] = pr.rush ? 1 : 0
    for (let a = 0; a < parts.anchorCount[s]; a++) parts.cut[s * MAX_ANCHORS_PER_PART + a] = pr.cut[a] ?? 0
    if (pr.contents !== undefined) parts.contents[s] = structuredClone(pr.contents)
    else if (parts.type[s] !== CARGO_POD_TYPE) parts.contents[s] = null
    if (pr.freed) restoreFreedPart(st, s, pr)
  }
  if (wreckEmpty(st, w)) {
    st.wrecks.looted[w] = 1
    st.lootedAt[w] = st.ctx.mirror.gameTime
  }
}

/** A freed part of a save: its pose and velocity, and a body. */
function restoreFreedPart(st: SalvageState, s: number, pr: WreckPartRecord): void {
  const parts = st.parts
  if (pr.pos) {
    parts.x[s] = pr.pos[0]
    parts.y[s] = pr.pos[1]
    parts.z[s] = pr.pos[2]
  }
  if (pr.vel) {
    parts.vx[s] = pr.vel[0]
    parts.vy[s] = pr.vel[1]
    parts.vz[s] = pr.vel[2]
  }
  if (pr.rot) {
    parts.qx[s] = pr.rot[0]
    parts.qy[s] = pr.rot[1]
    parts.qz[s] = pr.rot[2]
    parts.qw[s] = pr.rot[3]
  }
  parts.freed[s] = 1
  buildFreedBody(st, s)
}
